import { z } from 'zod';

import { crmDealStageSchema } from './crm-deal.schema';
import { crmLeadOriginSchema } from './crm-lead.schema';

/**
 * Pipeline dashboard schemas — Story 11.12 (FR-54).
 *
 * Tipos de RESPOSTA do dashboard de pipeline comercial:
 *  - cards de topo (com variação vs período anterior),
 *  - funil por estágio (valor + contagem + taxa de conversão entre estágios),
 *  - conversão lead→cliente por origem,
 *  - propostas a vencer (reusa o shape de proposta da listagem).
 *
 * Zod parse em TODAS as respostas (Tier 2 mandatório). Período/responsável são
 * filtros de query — validados no controller via `pipelineSummaryQuerySchema`.
 */

// -----------------------------------------------------------------------------
// Query (filtros globais — AC1/AC6)
// -----------------------------------------------------------------------------

/** Janela de tempo do dashboard. `all` = sem corte temporal. */
export const pipelinePeriodSchema = z.enum(['7d', '30d', '90d', 'all']);
export type PipelinePeriod = z.infer<typeof pipelinePeriodSchema>;

/**
 * Filtros do dashboard (query string). `responsibleUserId` opcional (default =
 * todos). `period` default `30d`. Strings vazias viram undefined (o front pode
 * mandar `responsibleUserId=` ao limpar o filtro).
 */
export const pipelineSummaryQuerySchema = z.object({
  period: pipelinePeriodSchema.default('30d'),
  // String vazia (filtro "Todos" limpo no front) vira undefined; senão exige UUID.
  responsibleUserId: z.preprocess(
    (v) => (v === '' || v === undefined || v === null ? undefined : v),
    z.string().uuid().optional(),
  ),
});
export type PipelineSummaryQuery = z.infer<typeof pipelineSummaryQuerySchema>;

// -----------------------------------------------------------------------------
// Cards de topo (AC2)
// -----------------------------------------------------------------------------

/**
 * Card de pipeline com variação vs período anterior. `delta_pct = null` quando
 * não há base anterior pra comparar (período = `all` ou denominador zero).
 * `value` é monetário OU contagem conforme o card.
 */
export const pipelineCardSchema = z.object({
  value: z.number(),
  delta_pct: z.number().nullable(),
  trend: z.enum(['up', 'down', 'flat', 'unknown']),
});
export type PipelineCard = z.infer<typeof pipelineCardSchema>;

/**
 * Cards de topo. Os 2 cards monetários (`total_in_negotiation`,
 * `proposals_expiring`/leads são contagem) viram `null` pra `suporte` (FR-59.3 —
 * sem valores estratégicos). Contagens são sempre visíveis.
 */
export const pipelineSummaryCardsSchema = z.object({
  /** Soma de `value` de deals `open` (monetário). `null` se role sem valores. */
  total_in_negotiation: pipelineCardSchema.nullable(),
  /** Contagem de deals `open`. */
  open_deals: pipelineCardSchema,
  /** Contagem de propostas enviada|visualizada a vencer em <= 7 dias. */
  proposals_expiring: pipelineCardSchema,
  /** Contagem de leads quentes sem próxima ação (e não descartados/convertidos). */
  hot_leads_no_action: pipelineCardSchema,
});
export type PipelineSummaryCards = z.infer<typeof pipelineSummaryCardsSchema>;

// -----------------------------------------------------------------------------
// Funil por estágio (AC3)
// -----------------------------------------------------------------------------

/**
 * Linha do funil: 1 estágio. `value` = soma do `value` dos deals open no estágio
 * (null pra role sem valores); `count` = contagem; `conversion_from_previous` =
 * taxa de conversão a partir do estágio anterior na ordem do funil (null no 1º).
 */
export const funnelStageSchema = z.object({
  stage: crmDealStageSchema,
  value: z.number().nullable(),
  count: z.number().int().nonnegative(),
  /** count(este) / count(anterior) * 100 — null no primeiro estágio. */
  conversion_from_previous: z.number().nullable(),
});
export type FunnelStage = z.infer<typeof funnelStageSchema>;

export const pipelineFunnelSchema = z.object({
  stages: z.array(funnelStageSchema),
  total_count: z.number().int().nonnegative(),
  /** Soma do valor de todos os estágios open (null pra role sem valores). */
  total_value: z.number().nullable(),
});
export type PipelineFunnel = z.infer<typeof pipelineFunnelSchema>;

// -----------------------------------------------------------------------------
// Conversão por origem (AC4)
// -----------------------------------------------------------------------------

/**
 * Conversão lead→cliente agrupada por origem. `conversions` = leads `convertido`;
 * `total` = leads da origem no período; `rate` = conversions/total*100 (0 se
 * total=0).
 */
export const conversionByOriginRowSchema = z.object({
  origin: crmLeadOriginSchema,
  conversions: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  rate: z.number(),
});
export type ConversionByOriginRow = z.infer<typeof conversionByOriginRowSchema>;

// -----------------------------------------------------------------------------
// Proposta a vencer (AC5) — subset do shape da listagem de propostas (11.6)
// -----------------------------------------------------------------------------

const expiringRelationSchema = z.object({ id: z.string(), name: z.string() }).nullable();

/**
 * Proposta a vencer (item da tabela). Espelha o `toProposalListApi` do backend,
 * mas só os campos que a tabela do dashboard usa (número, destinatário, produto,
 * valor, validade, responsável via deal). `responsible_user_id` vem do deal
 * vinculado quando existe.
 */
export const expiringProposalSchema = z.object({
  id: z.string().uuid(),
  proposal_number: z.string(),
  status: z.enum(['enviada', 'visualizada']),
  monthly_value: z.string().nullable(),
  annual_value: z.string().nullable(),
  valid_until: z.string(),
  saas_product: expiringRelationSchema,
  client: expiringRelationSchema,
  lead: expiringRelationSchema,
  deal: z.object({ id: z.string(), title: z.string() }).nullable(),
  responsible_user_id: z.string().nullable(),
});
export type ExpiringProposal = z.infer<typeof expiringProposalSchema>;

// -----------------------------------------------------------------------------
// Resposta completa (AC1)
// -----------------------------------------------------------------------------

export const pipelineSummaryResponseSchema = z.object({
  data: z.object({
    period: pipelinePeriodSchema,
    summary: pipelineSummaryCardsSchema,
    funnel: pipelineFunnelSchema,
    /** `null` pra `financeiro` (dado estratégico omitido — FR-59.3). */
    conversion_by_origin: z.array(conversionByOriginRowSchema).nullable(),
    proposals_expiring: z.array(expiringProposalSchema),
  }),
});
export type PipelineSummaryResponse = z.infer<typeof pipelineSummaryResponseSchema>;
export type PipelineSummaryData = PipelineSummaryResponse['data'];
